import { html, LitElement } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';

export class SimpleToast extends LitElement {
  static get properties() {
    return {
      position: { type: String }, // 'top-left', 'top-right', 'bottom-left', 'bottom-right'
      message: { type: Object }, // Can be string or Lit template
      duration: { type: Number }, // milliseconds, 0 or undefined = infinity
      dismissible: { type: Boolean },
      visible: { type: Boolean },
      fadeOut: { type: Boolean },
    };
  }

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.position = 'top-right';
    this.positionMobile = 'bottom-center';
    this.message = null;
    this.duration = 0;
    this.dismissible = true;
    this.visible = false;
    this.fadeOut = false;
    this.timeoutId = null;
    this.html = null;
    this.touchStartX = null;
    this.touchStartY = null;
  }

  get toastStyle() {
    const isMobile = window.innerWidth <= 767;
    const activePosition = isMobile
      ? this.positionMobile
      : this.position;

    if (activePosition?.startsWith('top-')) {
      const headerElement = document.querySelector('#header');
      const headerRect = headerElement?.getBoundingClientRect();
      return `top: ${(headerRect?.bottom || 0) + 16}px`;
    }

    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    requestAnimationFrame(() => {
      this.visible = true;
      this.startAutoRemove();
    });

    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    this.addEventListener('touchmove', this.handleTouchMove.bind(this), false);
    this.addEventListener('touchstart', this.handleTouchStart.bind(this), false);
  }

  getTouches(event) {
    return event.touches || event.originalEvent.touches;
  }

  handleTouchStart(event) {
    const firstTouch = this.getTouches(event)[0];
    this.touchStartX = firstTouch.clientX;
    this.touchStartY = firstTouch.clientY;
  }

  handleTouchMove(event) {
    if (!this.touchStartX || !this.touchStartY) {
      return;
    }

    const xUp = event.touches[0].clientX;
    const yUp = event.touches[0].clientY;

    const xDiff = this.touchStartX - xUp;
    const yDiff = this.touchStartY - yUp;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (xDiff > 0) {
        this.remove();
      } else {
        this.remove();
      }
    } else {
      if (yDiff > 0) {
        this.remove();
      } else {
        this.remove();
      }
    }

    this.touchStartX = null;
    this.touchStartY = null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.clearAutoRemove();
    window.removeEventListener('scroll', this.handleScroll.bind(this));
  }

  startAutoRemove() {
    if (!this.duration || this.duration <= 0) return;

    this.timeoutId = setTimeout(() => {
      this.remove();
    }, this.duration);
  }

  clearAutoRemove() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  handleScroll() {
    this.requestUpdate();
  }

  remove() {
    this.fadeOut = true;

    setTimeout(() => {
      if (this.parentNode) {
        this.parentNode.removeChild(this);
      }
    }, 300);
  }

  handleClick() {
    if (this.dismissible) {
      this.remove();
    }
  }

  render() {
    const isMobile = window.innerWidth <= 767;
    const activePosition = isMobile
      ? this.positionMobile
      : this.position;
    const positionClass = `toast_${activePosition.replace('-', '_')}`;
    const classes = [
      'toast',
      positionClass,
      this.visible
        ? 'toast_visible'
        : '',
      this.fadeOut
        ? 'toast_fade_out'
        : '',
      this.dismissible
        ? 'toast_dismissible'
        : '',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div class="${classes}" style=${ifDefined(this.toastStyle)}>
        <div class="toast_content">
          ${typeof this.message === 'string'
            ? unsafeHTML(this.message)
            : this.message}
        </div>
        ${this.dismissible
          ? html`
              <button class="toast_close" @click=${this.handleClick}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                </svg>
              </button>
            `
          : ''}
      </div>
    `;
  }
}

if (!customElements.get('simple-toast')) {
  customElements.define('simple-toast', SimpleToast);
}

class ToastManager {
  constructor() {
    this.currentToast = null;
  }

  show(message, options = {}) {
    const {
      position = 'top-right',
      positionMobile = 'bottom-center',
      duration = 0,
      dismissible = true,
    } = options;

    if (this.currentToast && this.currentToast.parentNode) {
      this.currentToast.remove();
    }

    const toast = document.createElement('simple-toast');
    toast.position = position;
    toast.positionMobile = positionMobile;
    toast.message = message;
    toast.duration = duration;
    toast.dismissible = dismissible;

    document.body.appendChild(toast);
    this.currentToast = toast;

    return toast;
  }

  hide() {
    if (this.currentToast) {
      this.currentToast.remove();
      this.currentToast = null;
    }
  }
}

const toastManager = new ToastManager();

function toast(message, options = {}) {
  return toastManager.show(message, options);
}

toast.hide = () => {
  toastManager.hide();
};

if (typeof window !== 'undefined') {
  window.toast = toast;
}

export { toast };
